/* eslint-disable max-lines */
import {
  chargeElavonCard,
  chargeElavonOnline,
  chargeSavedCard,
  getElavonOnlineCredentials
} from 'api/Transaction';

import { showAlert } from 'components/shared/Alert/Alert';

const DEFAULT_ERROR_MESSAGE =
  "There's been an error. Please try again later. If the issue persists please contact support.";

export const elavonPreAuthDeferredHandler = async (
  navigate,
  { billingAddress, invoiceToken, surchargeAmount, payerId, patientId, terminalId, preAuthForm }
) => {
  const credentialsParams = {
    amount: 0,
    saveCard: true,
    tokenOnly: true,
    billingAddress,
    invoiceToken,
    surcharge: surchargeAmount,
    payerId,
    terminalId
  };
  if (patientId) credentialsParams.patientId = patientId;
  const credentialsResponse = await getElavonOnlineCredentials(navigate, credentialsParams);
  const credentialsData = credentialsResponse?.data || credentialsResponse;
  if (credentialsData?.code !== 0 || !credentialsData?.token) {
    throw new Error(credentialsData?.error || DEFAULT_ERROR_MESSAGE);
  }

  return new Promise((resolve, reject) => {
    try {
      window.PayWithConverge.open(
        { ssl_txn_auth_token: credentialsData.token },
        {
          onError: (err) => reject(new Error(err?.errorMessage || DEFAULT_ERROR_MESSAGE)),
          onCancelled: () => reject(new Error('Payment cancelled.')),
          onDeclined: () => reject(new Error('Payment was declined.')),
          onApproval: (response) => {
            resolve({
              code: 0,
              deferred: true,
              processor: 'elavon',
              terminalId,
              preAuthForm,
              onlineCard: { ...response, terminal_id: terminalId }
            });
          }
        }
      );
    } catch (error) {
      reject(error);
    }
  });
};

export const elavonSavedCardHandler = (
  navigate,
  {
    invoices,
    appointmentId,
    surchargeAmount,
    amount,
    appliedBalance,
    invoiceToken,
    methodDetail,
    recurringPayment,
    planId,
    updatePlanId,
    scheduleId
  }
) => {
  if (!methodDetail) return;

  return chargeSavedCard(navigate, {
    amount,
    invoiceToken,
    invoices,
    appointmentId,
    cardId: methodDetail,
    surchargeAmount,
    appliedBalance,
    ...(recurringPayment && { recurringPayment }),
    ...(planId && { planId }),
    ...(updatePlanId && { updatePlanId }),
    ...(scheduleId && { scheduleId })
  });
};

const completeTransaction = async (
  navigate,
  {
    token,
    invoices,
    invoiceToken,
    transactionId,
    response,
    payerId,
    appliedBalance,
    recurringPayment,
    planId,
    updatePlanId,
    scheduleId,
    createPatientFromCCInfo
  }
) => {
  try {
    const cardHolderName = createPatientFromCCInfo
      ? [response?.ssl_first_name, response?.ssl_last_name].filter(Boolean).join(' ').trim()
      : undefined;

    const res = await chargeElavonOnline(navigate, {
      token,
      invoices,
      invoiceToken,
      transactionId,
      transaction: response,
      payerId,
      appliedBalance,
      ...(recurringPayment && { recurringPayment }),
      ...(planId && { planId }),
      ...(updatePlanId && { updatePlanId }),
      ...(scheduleId && { scheduleId }),
      ...(createPatientFromCCInfo &&
        cardHolderName && { createPatientFromCCInfo: true, cardHolderName })
    });

    const { code, redirect, error, cardId: _cardId, recurringError } = res;
    switch (code) {
      case -1:
        navigate(redirect);
        break;
      case 0: {
        if (recurringError) {
          showAlert({
            title: 'Payment processed but recurring plan setup failed. Please contact support.',
            color: 'warning'
          });
        }
        return res;
      }
      default:
        showAlert({ title: error || DEFAULT_ERROR_MESSAGE, color: 'danger' });
        break;
    }
  } catch (error) {
    console.error(error);
    showAlert({ title: error || DEFAULT_ERROR_MESSAGE, color: 'danger' });
  }
};

const openLightbox = async (
  navigate,
  token,
  transactionId,
  {
    invoices,
    invoiceToken,
    payerId,
    appliedBalance,
    tokenOnly,
    preAuthForm,
    terminalId,
    recurringPayment,
    planId,
    updatePlanId,
    scheduleId,
    patientId,
    createPatientFromCCInfo
  }
) => {
  return new Promise((resolve, reject) => {
    try {
      const callback = {
        async onError(error) {
          try {
            if (tokenOnly) {
              reject(new Error(DEFAULT_ERROR_MESSAGE));
              return;
            }

            const result = await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              response: { txnStatus: 'error', ...error }
            });
            reject(result);
          } catch (err) {
            reject(err);
          }
        },
        async onCancelled() {
          try {
            if (tokenOnly) {
              reject(new Error('Payment cancelled.'));
              return;
            }

            const result = await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              response: { txnStatus: 'cancelled' }
            });
            resolve(result);
          } catch (err) {
            reject(err);
          }
        },
        async onDeclined(response) {
          try {
            if (tokenOnly) {
              reject(new Error('Payment was declined.'));
              return;
            }

            const result = await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              response: { txnStatus: 'declined', ...response }
            });
            reject(result);
          } catch (err) {
            reject(err);
          }
        },
        async onApproval(response) {
          try {
            if (tokenOnly) {
              const result = await chargeElavonCard(navigate, {
                targetUserId: patientId,
                terminalId,
                tokenOnly,
                invoiceToken,
                payerId,
                onlineCard: { ...response, terminal_id: terminalId },
                preAuthForm,
                ...(recurringPayment && { recurringPayment }),
                ...(planId && { planId }),
                ...(updatePlanId && { updatePlanId }),
                ...(scheduleId && { scheduleId })
              });
              resolve(result);
              return;
            }

            const result = await completeTransaction(navigate, {
              token,
              invoices,
              invoiceToken,
              transactionId,
              payerId,
              appliedBalance,
              terminalId,
              recurringPayment,
              planId,
              updatePlanId,
              scheduleId,
              createPatientFromCCInfo,
              response: { txnStatus: 'approved', ...response }
            });
            resolve(result);
          } catch (err) {
            reject(err);
          }
        }
      };
      window.PayWithConverge.open({ ssl_txn_auth_token: token }, callback);
    } catch (error) {
      console.error(error);
      showAlert({
        title: error || DEFAULT_ERROR_MESSAGE,
        color: 'danger'
      });
      reject(error);
    }
  });
};

export const chargeElavonOnlineHandler = async (
  navigate,
  {
    amount,
    saveCard,
    tokenOnly,
    billingAddress,
    invoiceToken,
    surchargeAmount,
    payerId,
    patientId,
    appliedBalance,
    invoices,
    transactionId,
    preAuthForm,
    terminalId,
    recurringPayment,
    planId,
    updatePlanId,
    scheduleId,
    createPatientFromCCInfo
  }
) => {
  const isInstallmentsNoUpfront =
    recurringPayment?.type === 'installments' && !(Number(recurringPayment?.upfrontPayment) > 0);
  const effectiveTokenOnly = tokenOnly || isInstallmentsNoUpfront;
  const effectiveAmount = isInstallmentsNoUpfront ? 0 : amount;

  try {
    const credentialsResponse = await getElavonOnlineCredentials(navigate, {
      amount: effectiveAmount,
      saveCard: saveCard || !!recurringPayment,
      tokenOnly: effectiveTokenOnly,
      billingAddress,
      invoiceToken,
      surcharge: surchargeAmount,
      payerId,
      appliedBalance,
      patientId,
      terminalId,
      ...(recurringPayment && { recurringPayment })
    });
    const credentialsData = credentialsResponse?.data || credentialsResponse;

    if (credentialsData?.code === 0) {
      const finalTransactionId = credentialsData.transactionId || transactionId;
      return openLightbox(navigate, credentialsData.token, finalTransactionId, {
        invoices,
        invoiceToken,
        payerId,
        appliedBalance,
        tokenOnly: effectiveTokenOnly,
        saveCard: saveCard || !!recurringPayment,
        preAuthForm,
        terminalId,
        recurringPayment,
        planId,
        updatePlanId,
        scheduleId,
        patientId,
        createPatientFromCCInfo
      });
    }

    if (credentialsData?.code === 6) {
      throw new Error(
        'Recurring payments require saving the card on file. Please enable card saving and try again.'
      );
    }

    throw new Error(credentialsData?.error || DEFAULT_ERROR_MESSAGE);
  } catch (error) {
    console.error('Error in chargeElavonOnlineHandler:', error);
    throw new Error(error?.message || DEFAULT_ERROR_MESSAGE);
  }
};
